import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Paper,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  OutlinedInput,
  FormHelperText,
  Alert,
  CircularProgress,
} from "@mui/material";
import { EPath } from "constants/path";
import type { CompleteOnboardingRequest } from "libs/cellera-sdk";
import { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";

import { authenticationApi } from "../apis";
import { useAuth } from "../contexts/AuthContext";
import { useCelleraTheme } from "../theme/useCelleraTheme";
import {
  providerOnboardingStep1Schema,
  providerOnboardingStep2Schema,
} from "../validations";
import PaymentSelection from "./components/PaymentSelection";

// Onboarding steps enum to avoid hardcoded step numbers
enum OnboardingSteps {
  PROVIDER_STEP_1 = 0,
  PROVIDER_STEP_2 = 1,
  PROVIDER_PAYMENT = 2,
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const specialtyOptions = [
  { value: "general_practice", label: "General Practice" },
  { value: "cardiology", label: "Cardiology" },
  { value: "dermatology", label: "Dermatology" },
  { value: "endocrinology", label: "Endocrinology" },
  { value: "gastroenterology", label: "Gastroenterology" },
  { value: "neurology", label: "Neurology" },
  { value: "oncology", label: "Oncology" },
  { value: "orthopedics", label: "Orthopedics" },
  { value: "pediatrics", label: "Pediatrics" },
  { value: "psychiatry", label: "Psychiatry" },
];

// Provider form interfaces
interface ProviderStep1Data {
  licenseNumber: string;
  specialties: (string | undefined)[];
}

interface ProviderStep2Data {
  practiceId: string;
}

// Combined data for submission
interface OnboardingFormData {
  licenseNumber?: string;
  specialties?: string[];
  practiceId?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
}

const OnboardingPage: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<OnboardingSteps>(
    OnboardingSteps.PROVIDER_STEP_1
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<OnboardingFormData>({});
  const [formCompleted, setFormCompleted] = useState(false);
  const [isCheckingOnboarding, setIsCheckingOnboarding] = useState(true);
  const { user, userClaims, completeOnboarding, refreshUserClaims } = useAuth();
  const { colors } = useCelleraTheme();

  const isProvider = userClaims?.role === "provider";

  // Check if onboarding is already complete when component mounts or when returning from Stripe
  useEffect(() => {
    const checkOnboardingStatus = async () => {
      if (!isProvider || !user?.uid || !userClaims || formCompleted) {
        setIsCheckingOnboarding(false);
        return;
      }

      try {
        // Call checkOnboarding API to get onboarding status
        const response = await authenticationApi.checkOnboarding();

        // Check if onboarding is already complete
        // isOnboardingComplete === true means onboarding_complete === true in database
        // Also check requiresOnboarding === false as a fallback
        if (response.isOnboardingComplete === true) {
          // Onboarding is already complete, skip to payment step
          setFormCompleted(true);
          setCurrentStep(OnboardingSteps.PROVIDER_PAYMENT);
        }
      } catch {
        // If error, assume onboarding is not complete and start from step 1
        // Error is expected if there's a network issue or API error
      } finally {
        setIsCheckingOnboarding(false);
      }
    };

    void checkOnboardingStatus();
  }, [isProvider, user?.uid, userClaims, formCompleted]);

  // Poll for updated claims while on payment step to pick up requiresOnboarding=false after Stripe

  // If requiresOnboarding becomes false at any time (e.g., after Stripe success), redirect to dashboard
  useEffect(() => {
    if (isProvider && userClaims && userClaims.requiresOnboarding === false) {
      window.location.href = EPath.PROVIDER_DASHBOARD;
    }
  }, [isProvider, userClaims]);

  const providerStep1Form = useForm<ProviderStep1Data>({
    resolver: yupResolver(providerOnboardingStep1Schema),
    defaultValues: {
      licenseNumber: "",
      specialties: [],
    },
  });

  const providerStep2Form = useForm<ProviderStep2Data>({
    resolver: yupResolver(providerOnboardingStep2Schema),
    defaultValues: {
      practiceId: "",
    },
  });

  const providerSteps = [
    {
      title: "Professional Information",
      description: "License and specialties",
    },
    {
      title: "Practice Information",
      description: "Practice details",
    },
    ...(formCompleted
      ? [
          {
            title: "Payment",
            description: "Complete your subscription",
          },
        ]
      : []),
  ];

  const handleNext = async () => {
    setError(null);

    if (isProvider) {
      if (currentStep === OnboardingSteps.PROVIDER_STEP_1) {
        // Step 1: Validate and save step 1 data
        const isValid = await providerStep1Form.trigger();
        if (isValid) {
          const step1Data = providerStep1Form.getValues();
          setFormData((prev) => ({
            ...prev,
            licenseNumber: step1Data.licenseNumber,
            specialties: step1Data.specialties.filter(
              (specialty): specialty is string => specialty !== undefined
            ),
          }));
          setCurrentStep(OnboardingSteps.PROVIDER_STEP_2);
        }
      } else if (currentStep === OnboardingSteps.PROVIDER_STEP_2) {
        const isValid = await providerStep2Form.trigger();
        if (isValid) {
          const step2Data = providerStep2Form.getValues();
          const finalData = {
            ...formData,
            practiceId: step2Data.practiceId,
          };
          await handleSubmit(finalData);
        }
      }
    }
  };

  const handleBack = () => {
    if (isProvider && currentStep === OnboardingSteps.PROVIDER_STEP_2) {
      // Going back to step 1, restore step 1 data and reset step 2
      if (formData.licenseNumber || formData.specialties) {
        providerStep1Form.setValue(
          "licenseNumber",
          formData.licenseNumber || ""
        );
        providerStep1Form.setValue("specialties", formData.specialties || []);
      }
    }
    setCurrentStep((prevStep) => prevStep - 1);
    setError(null);
  };

  const handleSubmit = async (data: OnboardingFormData) => {
    setLoading(true);
    setError(null);

    try {
      // Always complete onboarding first
      const submitData = {
        uid: user?.uid,
        ...data,
      };

      await completeOnboarding(submitData as CompleteOnboardingRequest);
      setFormCompleted(true);

      // After completing onboarding, allow access if requiresOnboarding is now false
      if (isProvider) {
        await refreshUserClaims();
        if (userClaims && userClaims.requiresOnboarding === false) {
          window.location.href = EPath.PROVIDER_DASHBOARD;
        } else {
          setCurrentStep(OnboardingSteps.PROVIDER_PAYMENT);
        }
      } else {
        setCurrentStep(OnboardingSteps.PROVIDER_STEP_2);
      }
    } catch (error: unknown) {
      setError(
        error instanceof Error ? error.message : "Failed to complete onboarding"
      );
    } finally {
      setLoading(false);
    }
  };

  const renderProviderStep1 = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Controller
        key="step1-licenseNumber"
        name="licenseNumber"
        control={providerStep1Form.control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            fullWidth
            label="Medical License Number"
            placeholder="Enter your medical license number"
            error={!!error}
            helperText={error?.message}
            variant="outlined"
            autoComplete="off"
          />
        )}
      />

      <Controller
        key="step1-specialties"
        name="specialties"
        control={providerStep1Form.control}
        render={({ field, fieldState: { error } }) => (
          <FormControl fullWidth error={!!error}>
            <InputLabel>Medical Specialties</InputLabel>
            <Select
              {...field}
              multiple
              input={<OutlinedInput label="Medical Specialties" />}
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {(selected as string[]).map((value) => {
                    const option = specialtyOptions.find(
                      (opt) => opt.value === value
                    );
                    return (
                      <Chip
                        key={value}
                        label={option?.label || value}
                        size="small"
                      />
                    );
                  })}
                </Box>
              )}
              MenuProps={MenuProps}
            >
              {specialtyOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {error && <FormHelperText>{error.message}</FormHelperText>}
          </FormControl>
        )}
      />
    </Box>
  );

  const renderProviderStep2 = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Controller
        key="step2-practiceId"
        name="practiceId"
        control={providerStep2Form.control}
        render={({ field, fieldState: { error } }) => {
          return (
            <TextField
              fullWidth
              label="Practice ID"
              placeholder="Enter your practice ID"
              error={!!error}
              helperText={error?.message}
              variant="outlined"
              autoComplete="off"
              value={field.value || ""}
              onChange={(e) => {
                field.onChange(e.target.value);
              }}
            />
          );
        }}
      />
    </Box>
  );

  const renderPaymentStep = () => {
    // Check if subscription is active (billing complete)
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <PaymentSelection />
      </Box>
    );
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case OnboardingSteps.PROVIDER_STEP_1:
        return renderProviderStep1();
      case OnboardingSteps.PROVIDER_STEP_2:
        return renderProviderStep2();
      case OnboardingSteps.PROVIDER_PAYMENT:
        return renderPaymentStep();
      default:
        return null;
    }
  };

  // Show loading while checking onboarding status to avoid flashing step 1
  if (isCheckingOnboarding) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: colors.background.default,
          padding: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: colors.background.default,
        padding: 3,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 800,
          padding: 4,
          borderRadius: 2,
        }}
      >
        {/* Header */}
        <Box sx={{ textAlign: "center", marginBottom: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Complete Your Profile
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Let&apos;s set up your account with some additional information
          </Typography>
        </Box>

        {/* Stepper */}
        <Stepper activeStep={currentStep} sx={{ marginBottom: 4 }}>
          {providerSteps.map((step) => (
            <Step key={step.title}>
              <StepLabel>
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    {step.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {step.description}
                  </Typography>
                </Box>
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ marginBottom: 3 }}>
            {error}
          </Alert>
        )}

        {/* Form Content */}
        <Box sx={{ marginBottom: 4 }} key={`step-${currentStep}`}>
          {renderStepContent()}
        </Box>

        {/* Navigation Buttons */}
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Button
            onClick={handleBack}
            disabled={
              currentStep === OnboardingSteps.PROVIDER_STEP_1 || loading
            }
            variant="outlined"
          >
            Back
          </Button>

          {/* Hide Next/Complete button on payment step */}
          {!(
            (formCompleted &&
              ((isProvider &&
                currentStep === OnboardingSteps.PROVIDER_PAYMENT) ||
                (!isProvider &&
                  currentStep === OnboardingSteps.PROVIDER_STEP_2))) ||
            (isProvider && currentStep === OnboardingSteps.PROVIDER_PAYMENT)
          ) && (
            <Button
              onClick={handleNext}
              disabled={loading}
              variant="contained"
              sx={{
                backgroundColor: colors.primary.dark,
                "&:hover": {
                  backgroundColor: colors.primary.darker,
                },
                minWidth: 120,
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : currentStep === providerSteps.length - 1 ? (
                "Complete Setup"
              ) : (
                "Next"
              )}
            </Button>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default OnboardingPage;
